export type SportTabKey = "turfs" | "football" | "courts" | "badminton";

export interface SportTabConfig {
  key: SportTabKey;
  sport: string;
  label: string;
  icon: string;
}

export const SPORT_TABS: SportTabConfig[] = [
  {
    key: "turfs",
    sport: "CRICKET",
    label: "Cricket",
    icon: "/Icon/batball-icon.png",
  },
  {
    key: "football",
    sport: "FOOTBALL",
    label: "Football",
    icon: "/Icon/football-icon.png",
  },
  {
    key: "courts",
    sport: "PICKLEBALL",
    label: "Pickleball",
    icon: "/Icon/pickle-ball icon.png",
  },
  {
    key: "badminton",
    sport: "BADMINTON",
    label: "Badminton",
    icon: "/Icon/batball-icon.png",
  },
];

export const TAB_TO_SPORT: Record<string, string> = SPORT_TABS.reduce(
  (acc, tab) => ({ ...acc, [tab.key]: tab.sport }),
  {} as Record<string, string>
);

const readSupportedSports = (turf: any): string[] => {
  const sports = turf?.supportedSports ?? turf?.data?.supportedSports;
  if (!Array.isArray(sports)) return [];
  return sports
    .filter((sport: unknown) => typeof sport === "string")
    .map((sport: string) => sport.toUpperCase());
};

/**
 * Turfs saved before the multi-sport rollout carry no `supportedSports`, so they
 * count as cricket to stay visible on the default tab.
 */
export const turfSupportsSport = (turf: any, sport: string): boolean => {
  const target = sport?.toUpperCase();
  if (!target) return true;

  const turfSports = readSupportedSports(turf);
  if (turfSports.length === 0) return target === "CRICKET";
  return turfSports.includes(target);
};

/**
 * Filters a listing down to the turfs that support the given tab's sport.
 * Unknown tab keys leave the list untouched.
 */
export const filterTurfsBySportTab = <T,>(turfs: T[], activeTab: string): T[] => {
  const sport = TAB_TO_SPORT[activeTab];
  if (!sport || !Array.isArray(turfs)) return turfs;
  return turfs.filter((turf) => turfSupportsSport(turf, sport));
};

/**
 * Derives which sport tabs a listing should expose from the turfs it actually
 * has. Turfs saved before the multi-sport rollout carry no `supportedSports`,
 * so they count as cricket to keep older cities on the default tab.
 */
export const getAvailableSportTabs = (turfs: any[]): SportTabKey[] => {
  if (!Array.isArray(turfs) || turfs.length === 0) return [];

  const sports = new Set<string>();

  turfs.forEach((turf) => {
    const turfSports = readSupportedSports(turf);
    if (turfSports.length === 0) {
      sports.add("CRICKET");
      return;
    }
    turfSports.forEach((sport) => sports.add(sport));
  });

  const availableTabs = SPORT_TABS.filter((tab) => sports.has(tab.sport)).map(
    (tab) => tab.key
  );

  return availableTabs.length > 0 ? availableTabs : ["turfs"];
};
